import { DestroyRef, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, Observable, distinctUntilChanged, filter } from 'rxjs';
import { LocalStorageService, StorageKeys } from '../local-storage/local-storage.service';
import { School } from '@shared/types/school.model';

@Injectable({ providedIn: 'root' })
export class SchoolContextService {
  private currentSchoolSubject: BehaviorSubject<School | null>;
  currentSchool$;

  constructor(private storage: LocalStorageService) {
    // 🔥 restore user from localStorage on service init
    const storedSchool = this.storage.getItem<School>(StorageKeys.CurrentSchool);

    console.log("RESTORED SCHOOL FROM STORAGE:", storedSchool);

    this.currentSchoolSubject = new BehaviorSubject<School | null>(storedSchool);
    // Restoring from storage and re-confirming the same school after the school list loads both
    // call setCurrentSchool() with a new object reference for the same school — dedupe by id so
    // every subscriber (SchoolAccessScopeService, page components) doesn't re-fetch.
    this.currentSchool$ = this.currentSchoolSubject.asObservable().pipe(
      distinctUntilChanged((a, b) => a?.id === b?.id),
    );
  }

  setCurrentSchool(school: School | null) {
    this.currentSchoolSubject.next(school);
    if (school) {
      this.storage.setItem(StorageKeys.CurrentSchool, school);
    } else {
      this.storage.removeItem(StorageKeys.CurrentSchool);
    }
  }

  getCurrentSchool(): School | null {
    return this.currentSchoolSubject.getValue();
  }

  getSchoolTenantCode(): string | null {
    const school = this.getCurrentSchool();
    return school ? school.tenantCode : null;
  }

  getSchoolCode(): string | null {
    const school = this.getCurrentSchool();
    return school ? school.code : null;
  }

  clear() {
    this.setCurrentSchool(null);
  }

  /** Emits when navbar school selection changes (non-null, distinct by id). */
  distinctSchoolChanges$(): Observable<School> {
    return this.currentSchool$.pipe(
      filter((s): s is School => !!s),
      distinctUntilChanged((a, b) => a.id === b.id),
    );
  }

  /** Subscribe to school changes; auto-unsubscribes when destroyRef is destroyed. */
  onSchoolChange(destroyRef: DestroyRef, handler: (school: School) => void): void {
    this.distinctSchoolChanges$()
      .pipe(takeUntilDestroyed(destroyRef))
      .subscribe(handler);
  }
}
